/**
 * DEFUSE S10 — the ARC / ELLIPSE angle-and-axis mapping family: the exact
 * reflection table for circular endpoint angles, its direction-vector
 * siblings, the ellipse-image (conjugate-diameter / principal-axis) transform,
 * and the ARC → ELLIPSE promotion source the non-uniform arm builds.
 *
 * Split out of `./blockTransform.ts` (which re-exports the public surface and
 * carries the family's module documentation) purely for file size — the math
 * is unchanged. Depends only on `./blockInsertTransform.ts`.
 */

import { computeExplodedEllipseGeometry } from "@dxf-ai-editor/command-engine";
import type { ExplodeAffineMatrix } from "@dxf-ai-editor/command-engine";
import { ellipseEntitySchema } from "@dxf-ai-editor/drawing-model";
import type { ArcEntity, EllipseEntity } from "@dxf-ai-editor/drawing-model";

import type { Vec3 } from "../../types";

import {
  DEG_TO_RAD,
  TWO_PI,
  degToRad,
  isMirroredTransform,
  normalizeDeg,
  normalizeRad,
  preRotationCs,
  transformBlockDirection,
  type BlockInsertTransform,
} from "./blockInsertTransform";

/**
 * Map a circular-arc endpoint ANGLE (degrees, CCW from +X) through the
 * linear part — used for hatch arc-edge angles. EXACT closed-form (no trig,
 * so a `start == end (mod 360)` full-circle edge stays angle-equal after the
 * map — the downstream `normalizeSweep` full-circle guard depends on it):
 *
 *   sx>0, sy>0:  θ' = θ + rot            (pure rotation·scale)
 *   sx<0, sy<0:  θ' = θ + rot + 180      (point reflection = 180° turn)
 *   sx<0, sy>0:  θ' = rot + 180 − θ      (mirror across the local Y axis)
 *   sx>0, sy<0:  θ' = rot − θ            (mirror across the local X axis)
 *
 * Exact under uniform-|scale| (incl. mirror); under non-uniform scale a
 * circular arc really becomes elliptical — that promotion is the S10-XFORM
 * delta and this endpoint mapping is the documented baseline approximation.
 */
export function mapUniformArcAngleDeg(
  angleDeg: number,
  t: BlockInsertTransform,
): number {
  const negX = t.scale.x < 0;
  const negY = t.scale.y < 0;
  // An inner rotation acts on the SOURCE direction, so it simply shifts the
  // source angle before the table (identity — and the byte-identical
  // expressions below — when absent). Both endpoints shift by the same
  // amount, so the `start == end (mod 360)` full-circle guard survives.
  const pre = t.preRotationDeg;
  const a = pre ? angleDeg + pre : angleDeg;
  if (negX && negY) return normalizeDeg(a + t.rotationDeg + 180);
  if (negX) return normalizeDeg(t.rotationDeg + 180 - a);
  if (negY) return normalizeDeg(t.rotationDeg - a);
  return normalizeDeg(a + t.rotationDeg);
}

/**
 * Direction-angle map in DEGREES for hatch pattern directions (@codex #431
 * round-3): under uniform-|scale| (incl. mirror) it delegates to the EXACT
 * closed-form table above; under NON-uniform scale a direction genuinely
 * shears, so the direction VECTOR rides the linear transform and the mapped
 * angle is its `atan2` — e.g. a 45° ANSI31 direction under `scaleX 2 /
 * scaleY 1` maps to `atan2(sin45°, 2·cos45°) ≈ 26.57°`, matching the scaled
 * boundary. (Hatch ARC-EDGE endpoints deliberately do NOT use this general
 * branch: their non-uniform image is elliptic — the S10-XFORM promotion —
 * and the exact table preserves the full-circle `start == end` guard.)
 */
export function mapDirectionAngleDeg(
  angleDeg: number,
  t: BlockInsertTransform,
): number {
  if (Math.abs(t.scale.x) === Math.abs(t.scale.y)) {
    return mapUniformArcAngleDeg(angleDeg, t);
  }
  const rad = degToRad(angleDeg);
  const dir = transformBlockDirection(
    { x: Math.cos(rad), y: Math.sin(rad), z: 0 },
    t,
  );
  return normalizeDeg((Math.atan2(dir.y, dir.x) * 180) / Math.PI);
}

/**
 * RADIAN twin of {@link mapDirectionAngleDeg} for direction angles stored in
 * radians (hatch GRADIENT rotation, DXF 460): exact four-branch reflection
 * table under uniform-|scale|, vector-through-the-linear-map under
 * non-uniform scale (@codex #431 round-3 sibling of the patternAngle fix).
 * Kept separate from the degree version on purpose — the degree version's
 * integer-degree arithmetic keeps full-circle arc edges angle-equal
 * bit-exactly, and routing degrees through radians would destroy that.
 */
export function mapDirectionRad(
  angleRad: number,
  t: BlockInsertTransform,
): number {
  if (Math.abs(t.scale.x) !== Math.abs(t.scale.y)) {
    const dir = transformBlockDirection(
      { x: Math.cos(angleRad), y: Math.sin(angleRad), z: 0 },
      t,
    );
    return normalizeRad(Math.atan2(dir.y, dir.x));
  }
  const rot = degToRad(t.rotationDeg);
  const negX = t.scale.x < 0;
  const negY = t.scale.y < 0;
  // Inner-rotation shift of the SOURCE angle — the radian twin of
  // `mapUniformArcAngleDeg`'s (identity when absent).
  const pre = t.preRotationDeg;
  const a = pre ? angleRad + degToRad(pre) : angleRad;
  const mapped =
    negX && negY
      ? a + rot + Math.PI
      : negX
        ? rot + Math.PI - a
        : negY
          ? rot - a
          : a + rot;
  return normalizeRad(mapped);
}

/** Ellipse geometry fields shared by `ellipseEntitySchema` (params in
 *  RADIANS, `majorAxisEndpoint` RELATIVE to center). `center.z` AND
 *  `majorAxisEndpoint.z` are OPTIONAL (SL-15 round-6 @codex F1): the model
 *  points are optional-z, but the transformed clone must CARRY the block
 *  z-collapse (center) + the tilted major-axis z so `ellipseFromEntity` (via
 *  `optionalPointZ`) lifts the child like its Line/Circle/Arc siblings AND
 *  renders a tilted block-nested ellipse in its true plane instead of flat
 *  (the first cut narrowed the major axis to `{x, y}` and dropped the tilt).
 *  `extrusion` (DXF 210/220/230, the OCS plane normal) rides through too —
 *  the transform returns the FULL transformed normal so the render minor-axis
 *  direction stays correct under mirror / tilt (byte-identical (0,0,1) for a
 *  planar child under an orientation-preserving INSERT). */
export interface EllipseGeometryFields {
  center: { x: number; y: number; z?: number };
  majorAxisEndpoint: { x: number; y: number; z?: number };
  ratio: number;
  startParam: number;
  endParam: number;
  extrusion?: { x: number; y: number; z: number };
}

/** The implicit OCS normal of an ellipse that omits 210/220/230. */
const DEFAULT_ELLIPSE_NORMAL: Vec3 = { x: 0, y: 0, z: 1 };

/**
 * Build the `ExplodeAffineMatrix` (SVG column-major a,b,c,d = the 2×2 linear
 * part L, e,f = translation) that reproduces the block INSERT's 2D map on XY —
 * the EXACT same affine `transformBlockChildPoint` applies
 * (`world = R(rot)·S(scale)·(p − basePoint) + insertion`), folding the
 * basePoint subtract into e/f. Passed to the shared
 * `computeExplodedEllipseGeometry` so the render flatten sees the identical
 * transform the point/direction helpers do (verified: `m.a·p.x + m.c·p.y + e`
 * ≡ `transformBlockChildPoint`, `m.a·v.x + m.c·v.y` ≡ `transformBlockDirection`).
 */
function blockInsertAffine(
  basePoint: Vec3,
  t: BlockInsertTransform,
): ExplodeAffineMatrix {
  const rad = degToRad(t.rotationDeg);
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const a0 = t.scale.x * cos;
  const b0 = t.scale.x * sin;
  const c0 = -t.scale.y * sin;
  const d0 = t.scale.y * cos;
  // Right-multiply by the inner rotation — `L' = L · R(pre)`, the matrix twin
  // of the `preRotationCs` branch in the point/direction helpers (identity, and
  // the byte-identical columns above, when absent).
  const pre = preRotationCs(t);
  const a = pre ? a0 * pre.cos + c0 * pre.sin : a0;
  const b = pre ? b0 * pre.cos + d0 * pre.sin : b0;
  const c = pre ? c0 * pre.cos - a0 * pre.sin : c0;
  const d = pre ? d0 * pre.cos - b0 * pre.sin : d0;
  return {
    a,
    b,
    c,
    d,
    e: t.insertion.x - a * basePoint.x - c * basePoint.y,
    f: t.insertion.y - b * basePoint.x - d * basePoint.y,
  };
}

/**
 * Transform an ellipse under the block INSERT — SL-15 round-6 @codex F1 now
 * routes through the ONE-TRUTH 3D helper `computeExplodedEllipseGeometry`
 * (`@dxf-ai-editor/command-engine`), the SAME math the EXPLODE reducer
 * (`state/blockReducerHelpers.ts`) + the shell-preview mutation
 * (`command-engine/blockMutations.ts`) use, instead of the prior XY-only
 * conjugate-diameter clone. That helper:
 *
 *   - transforms `center` (DXF 10/20/30) through the full affine + block
 *     z-collapse (`insertionZ + scaleZ·(center.z − basePointZ)`);
 *   - transforms the FULL 3D major axis (11/21/31 — XY through L, z·scaleZ),
 *     so a TILTED block-nested ellipse keeps its out-of-plane major axis
 *     instead of rendering flat (the F1 defect);
 *   - recomputes `ratio` from the true 3D minor/major image lengths;
 *   - maps the OCS normal (210/220/230) through the cofactor map — a mirror
 *     (det < 0) materialises the flipped (0,0,−1) plane, which ENCODES the
 *     handedness. Because the normal carries the handedness AND the image
 *     major = L·(source major) preserves the parameter anchor, the params stay
 *     UNCHANGED (no re-anchoring — byte-identical to the reducer/explode; the
 *     old XY-only path re-anchored `[2π−end, 2π−start]` for a mirror because it
 *     could not carry the flipped normal).
 *
 * A planar child under an orientation-preserving INSERT stays byte-identical
 * (no spurious major/center z, the normal collapses back to (0,0,1)).
 */
export function transformEllipseGeometry(
  e: EllipseGeometryFields,
  basePoint: Vec3,
  t: BlockInsertTransform,
): EllipseGeometryFields {
  const result = computeExplodedEllipseGeometry(
    {
      center: e.center,
      majorAxisEndpoint: e.majorAxisEndpoint,
      ratio: e.ratio,
      extrusion: e.extrusion ?? DEFAULT_ELLIPSE_NORMAL,
      // Pass the arc params so the helper re-anchors them to the recovered
      // principal major-axis frame when a rotated/non-uniform INSERT rotates it
      // (Rytz branch) or swaps the longest axis (SL-15 round-8 @codex F2). A full
      // ellipse / frame-preserving case returns them byte-identical.
      startParam: e.startParam,
      endParam: e.endParam,
    },
    {
      matrix: blockInsertAffine(basePoint, t),
      scaleZ: t.scale.z,
      insertionZ: t.insertion.z,
      basePointZ: basePoint.z,
    },
  );
  return {
    center: result.center,
    majorAxisEndpoint: result.majorAxisEndpoint,
    ratio: result.ratio,
    // Re-anchored params from the helper (byte-identical to the input for a full
    // ellipse / no-frame-change case; ?? guards the params-absent contract).
    startParam: result.startParam ?? e.startParam,
    endParam: result.endParam ?? e.endParam,
    extrusion: result.extrusion,
  };
}

/**
 * The CCW sweep in DEGREES that `sampleArc` (`batchedLayer.ts`) walks for these
 * two group-50/51 angles: a negative difference is normalised by one turn, a
 * difference PAST a full turn is left alone (the sampler over-draws), and `0`
 * means the sampler emits COINCIDENT points — it draws NOTHING. Both the
 * promotion carve-out and the mirrored arm's endpoint rebuild read this.
 *
 * The WRAP decision is made on the RADIAN difference the sampler ACTUALLY
 * computes (`buildArcSceneGeometry` converts each endpoint separately, then
 * subtracts): a DENORMAL degree difference underflows to ∓0 there and so does
 * NOT wrap. Deciding it in degrees instead would turn an arc the sampler draws
 * as nothing into a full turn. The returned value itself stays in DEGREES and
 * exact — `90`, not `90.00000000000001` — because the scalar-angle conventions
 * around it are compared bit-for-bit.
 */
export function arcCcwSweepDeg(startAngleDeg: number, endAngleDeg: number): number {
  const sweepRad = endAngleDeg * DEG_TO_RAD - startAngleDeg * DEG_TO_RAD;
  if (sweepRad < 0) return endAngleDeg - startAngleDeg + 360;
  return sweepRad === 0 ? 0 : endAngleDeg - startAngleDeg;
}

/**
 * The smallest CCW sweep (RADIANS) the ELLIPSE form can hold.
 * `computeExplodedEllipseGeometry` ports ezdxf's
 * `ConstructionEllipse.transform` param tail, whose `isclose(abs_tol=1e-9)`
 * reads a narrower span as DEGENERATE and rewrites it to the full `0 … 2π`
 * revolution — so promoting a hair-thin arc would turn an invisible dot into a
 * whole drawn ellipse. (A span within 1e-9 OF 2π is consistent by the same
 * rule: it collapses to the full turn the arc sampler already draws.)
 */
export const MIN_PROMOTABLE_SWEEP_RAD = 1e-9;

/**
 * The block-child ARC as the ELLIPSE it is a member of — the LOCAL (block
 * frame) source the non-uniform promotion hands to
 * {@link transformEllipseGeometry}.
 *
 * A CIRCLE is the `ratio === 1`, `majorAxisEndpoint = (r, 0)` member of the
 * ellipse family, and for THAT member the parameter IS the polar angle — so
 * the ARC's group-50/51 angles become the group-41/42 params verbatim, in
 * radians. Promotion and the transform of an already-elliptic child are then
 * the SAME call, not two mechanisms. `ellipseEntitySchema.parse` strips the
 * Arc-only keys (`radius` / `startAngle` / `endAngle`) and fills the Ellipse
 * defaults, so the result is a REAL `EllipseEntity` for
 * `entityToRenderableTyped`; the shell fields ride the `...child` spread.
 *
 * The optional INNER rotation ({@link BlockInsertTransform.preRotationDeg}) is
 * BAKED INTO THIS SOURCE — a centre pre-rotated about the base point plus `+φ`
 * on both PARAMETERS, the frame left axis-aligned — and the caller strips it
 * from the transform it passes on. An ARC carries a rotation in its ANGLES, so
 * that is an exact re-parameterisation of the same map, and it is load-bearing:
 * with φ on the MATRIX the two transformed source semi-axes are conjugate but
 * NOT orthogonal, which sends the shared helper down its RYTZ principal-axis
 * branch — and THAT branch has a PRE-EXISTING parameter/normal inconsistency,
 * verified at this commit on an ELLIPSE child, which reaches it today with no
 * ARC involved (a ratio-1 ellipse under `preRotationDeg 37` + `S(2,3)` comes
 * back with the flipped (0,0,−1) plane and a start parameter that draws the
 * wrong point of the right ellipse). Owner:
 * `packages/command-engine/src/ellipseExplodeGeometry.ts` — not this module's
 * to fix; an axis-aligned source frame keeps the promotion on the exact
 * closed-form branch in every case, so it never depends on that resolution.
 *
 * The OCS normal is pinned to the WCS default rather than carried from
 * `child.extrusion` ON PURPOSE: this module is the OCS-free baseline affine
 * (extrusion OCS→WCS is its own S10-XFORM row) and the CIRCULAR arm likewise
 * never applies an ARC's own 210/220/230. Threading it here — where
 * `ellipseFromEntity` DOES consume the normal (SL-15 F-C) — would make an
 * OCS-flipped arc render differently under a non-uniform insert than under a
 * uniform one, i.e. would make the promotion carry a second, unrelated semantic
 * change. The promoted child's normal is therefore purely the IMAGE of
 * (0,0,1): unchanged for an orientation-preserving map, and the flipped
 * (0,0,−1) that ENCODES the handedness for a reflecting one.
 */
export function arcChildAsLocalEllipse(
  child: ArcEntity,
  basePoint: Vec3,
  preRotationDeg: number | undefined,
): EllipseEntity {
  const startParam = child.startAngle * DEG_TO_RAD;
  // The CCW span, CLAMPED to what the ELLIPSE form can hold: its parameters are
  // a `0 … 2π` range and the renderer reads a span at or past a full turn as
  // CLOSED, joining the last sample back to the first — so an un-clamped 405°
  // arc would gain a spurious chord across the ellipse. Clamped, that closing
  // segment is degenerate and the child draws the full sweep the arc drew.
  const sweep = Math.min(
    arcCcwSweepDeg(child.startAngle, child.endAngle) * DEG_TO_RAD,
    TWO_PI,
  );
  // The optional INNER rotation is baked into the SOURCE here instead of riding
  // the matrix (see the doc above), exactly the way an ARC carries a rotation:
  // in its ANGLES, with the frame left alone. Rotate the centre about the block
  // base point and ADD φ to both parameters; the major axis stays `(r, 0)`.
  // `c + R(φ)·(r cos θ, r sin θ) ≡ (R(φ)·(c − base) + base) + r(cos(θ+φ),
  // sin(θ+φ))` — an exact re-parameterisation of the same map, and one that
  // keeps the source semi-axes ALIGNED with the enclosing scale's axes, which
  // is what makes their images the principal pair.
  const preRad = preRotationDeg ? degToRad(preRotationDeg) : 0;
  const cos = preRotationDeg ? Math.cos(preRad) : 1;
  const sin = preRotationDeg ? Math.sin(preRad) : 0;
  const lx = child.center.x - basePoint.x;
  const ly = child.center.y - basePoint.y;
  const center = preRotationDeg
    ? {
        x: lx * cos - ly * sin + basePoint.x,
        y: lx * sin + ly * cos + basePoint.y,
        ...(child.center.z !== undefined ? { z: child.center.z } : {}),
      }
    : child.center;
  return ellipseEntitySchema.parse({
    ...child,
    type: "Ellipse",
    center,
    majorAxisEndpoint: { x: child.radius, y: 0 },
    ratio: 1,
    startParam: startParam + preRad,
    endParam: startParam + preRad + sweep,
    extrusion: DEFAULT_ELLIPSE_NORMAL,
  });
}

/**
 * Principal axes of the IMAGE of an ellipse under the block linear part —
 * the shared core of {@link transformEllipseGeometry} and the hatch
 * `ellipticArc` edge arm (@codex #431 P2: the edge must recompute
 * `minorRatio` and re-anchor its angles, not just map `majorAxisEnd`).
 */
export interface TransformedEllipseAxes {
  /** New major semi-axis vector (image principal axis, relative to centre). */
  major: { x: number; y: number };
  /** New minor/major ratio (≤ 1). */
  ratio: number;
  /** SOURCE parameter (radians) at which the new major axis sits; the image
   *  parameter of source parameter θ is ±(θ − tMajor). */
  tMajor: number;
  /** true → the image parameterisation runs CW (handedness flipped, i.e.
   *  det(L) < 0): image param = −(θ − tMajor), sweep direction inverts. */
  flip: boolean;
}

export function transformEllipseAxes(
  majorAxisEndpoint: { x: number; y: number },
  ratio: number,
  t: BlockInsertTransform,
): TransformedEllipseAxes {
  const m = majorAxisEndpoint;
  const u = transformBlockDirection({ x: m.x, y: m.y }, t);

  // Uniform-|scale| fast path (incl. mirror) — EXACT, no trig/hypot noise:
  // the linear part maps the principal frame onto (u, ±ratio·rot90(u)), so
  // the axes stay principal, the ratio is preserved and the param anchor is
  // 0 (params relative to the major axis are invariant; only the sweep
  // direction flips under a mirror).
  if (Math.abs(t.scale.x) === Math.abs(t.scale.y)) {
    return {
      major: { x: u.x, y: u.y },
      ratio,
      tMajor: 0,
      flip: isMirroredTransform(t),
    };
  }

  // n = ratio · rot90(m) — the minor semi-axis in the source frame; (u, v)
  // are conjugate semi-diameters of the image ellipse.
  const n = { x: -m.y * ratio, y: m.x * ratio };
  const v = transformBlockDirection(n, t);

  const uu = u.x * u.x + u.y * u.y;
  const vv = v.x * v.x + v.y * v.y;
  const uv = u.x * v.x + u.y * v.y;

  // Principal-axis parameter of A(t) = u·cos t + v·sin t.
  const t0 = 0.5 * Math.atan2(2 * uv, uu - vv);
  const cosT0 = Math.cos(t0);
  const sinT0 = Math.sin(t0);
  const axis1 = { x: u.x * cosT0 + v.x * sinT0, y: u.y * cosT0 + v.y * sinT0 };
  const axis2 = {
    x: -u.x * sinT0 + v.x * cosT0,
    y: -u.y * sinT0 + v.y * cosT0,
  };
  const len1 = Math.hypot(axis1.x, axis1.y);
  const len2 = Math.hypot(axis2.x, axis2.y);

  const majorIsAxis1 = len1 >= len2;
  const major = majorIsAxis1 ? axis1 : axis2;
  const majorLen = majorIsAxis1 ? len1 : len2;
  const minorLen = majorIsAxis1 ? len2 : len1;
  const tMajor = majorIsAxis1 ? t0 : t0 + Math.PI / 2;

  // Degenerate (zero-length axes after a degenerate scale) — pass through;
  // the schema's positive-ratio guard upstream makes this unreachable for
  // valid input.
  if (majorLen < 1e-12) {
    return { major: { x: u.x, y: u.y }, ratio, tMajor: 0, flip: false };
  }

  // Handedness of the image frame: cross(major, minor-at-tMajor+90°). The
  // minor direction A(tMajor+π/2) equals ±rot90(major)·ratio'; a negative
  // cross means the image parameterisation runs CW (mirror) → θ' = −φ.
  // (When axis2 is the major, A(tMajor+π/2) = A(t0+π) = −axis1.)
  const minorVec = majorIsAxis1 ? axis2 : { x: -axis1.x, y: -axis1.y };
  const cross = major.x * minorVec.y - major.y * minorVec.x;

  return {
    major: { x: major.x, y: major.y },
    ratio: Math.min(1, minorLen / majorLen),
    tMajor,
    flip: cross < 0,
  };
}
